/**
 * Shared form helpers for the question structure.
 *
 * Provides `<RichErrorMessage>` for inline validation feedback and
 * `useQuestionNameValidation`, a hook that enforces the min/max name-length
 * rules from the shared oRPC contract.
 */

import { SPACE_NAME_MAX_LENGTH, SPACE_NAME_MIN_LENGTH } from '@repo/shared/orpc/procedures/spaces';
import { useTheme } from '@repo/theme';
import { RichText } from 'component/richtext';
import { checkValidLength } from 'component/text';
import { useCallback } from 'react';
import type { TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';

type RichErrorMessageProps = {
    error: TLabel;
    formatParams?: Record<string, string>;
};

function RichErrorMessage({ error, formatParams }: RichErrorMessageProps) {
    const { colors } = useTheme();
    return <RichText color={colors.colorTextWarning} formatParams={formatParams} label={error} type='small' />;
}

export function useQuestionNameValidation(setError: (error: React.JSX.Element | undefined) => void) {
    const handleValidation = useCallback(
        (value: string) => {
            if (value.length === 0) {
                setError(<RichErrorMessage error={t('question.error_empty_prompt', 'The prompt cannot be empty.')} />);
            } else if (!checkValidLength({ text: value, min: SPACE_NAME_MIN_LENGTH, max: SPACE_NAME_MAX_LENGTH })) {
                setError(
                    <RichErrorMessage
                        error={t('question.error_prompt_too_long', 'The prompt must be fewer than {num} characters.')}
                        formatParams={{ num: SPACE_NAME_MAX_LENGTH.toString() }}
                    />,
                );
            } else {
                setError(undefined);
            }
        },
        [setError],
    );
    return handleValidation;
}
